/**
 * Compound Data Shortcut — "rank by energy", "which compound scored
 * best", "filter by MW under 500", "show me the scores".
 *
 * Reads anonymized numerical data from the workspace (energies, GNINA
 * scores, molecular properties) and produces a formatted summary in
 * chat. Deterministic, zero LLM, zero-latency.
 *
 * **Privacy boundary:** only anonymous indices + numerical values.
 * No compound names, SMILES, or structural identity.
 *
 * @module gibbs/knowledge/compoundDataShortcut
 */

// ─── Phrase detection ──────────────────────────────────────────────────────

// Keep TIGHT — canonical phrases only. The LLM handles fuzzy variants.
const COMPOUND_DATA_PHRASES = [
    'show scores',
    'show energies',
    'rank compounds',
    'compare compounds',
    'compound summary',
    'filter by mw',
    'filter by logp',
    'filter by energy',
    // Superlative phrasings (§8 corpus exemplar). Catches "which
    // compound scored best" and similar — natural NL questions about
    // ranking that the rest of the list misses.
    'scored best',
    'best compound',
    'best score',
];

/**
 * @param {string} userMessage
 * @returns {boolean}
 */
export function matchCompoundDataIntent(userMessage) {
    if (!userMessage || typeof userMessage !== 'string') return false;
    const lower = userMessage.toLowerCase();
    return COMPOUND_DATA_PHRASES.some((p) => lower.includes(p));
}

// ─── Data formatting ───────────────────────────────────────────────────────

/**
 * Format a number for display, handling nulls.
 * @param {number|null} val
 * @param {number} [decimals=1]
 * @returns {string}
 */
function fmt(val, decimals = 1) {
    if (val == null) return '—';
    return val.toFixed(decimals);
}

/**
 * Build a clean text summary of compound data.
 * Uses a vertical list format (not a table) so it doesn't overflow
 * in the narrow Gibbs chat pane.
 *
 * @param {Array<Object>} compounds - From getCompoundData()
 * @param {string} [sortBy='interactionScore'] - Key to sort by
 * @param {boolean} [ascending=true] - Sort direction (true = best first for energies)
 * @returns {{text: string, missingEnergies: number[]}} Summary +
 *   IDs of compounds missing energies
 */
export function buildCompoundSummary(compounds, sortBy, ascending) {
    if (!compounds || compounds.length === 0) {
        return {
            text: 'No compounds are loaded in the workspace.',
            missingEnergies: [],
        };
    }

    const hasEnergies = compounds.some((c) => c.energies);
    const hasProps = compounds.some((c) => c.properties);

    if (!hasEnergies && !hasProps) {
        return {
            text: `**${compounds.length}** compound(s) loaded, but no `
                + 'energies or properties have been calculated yet. '
                + 'Want me to run **get-energies** on them?',
            missingEnergies: compounds.map((c) => c.id),
        };
    }

    // Sort
    const effectiveSortBy = sortBy || (hasEnergies ? 'interactionScore' : 'MW');
    const effectiveAscending = ascending !== undefined ? ascending : true;
    const sorted = [...compounds].sort((a, b) => {
        const aVal = getNestedValue(a, effectiveSortBy);
        const bVal = getNestedValue(b, effectiveSortBy);
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        return effectiveAscending ? aVal - bVal : bVal - aVal;
    });

    // Build vertical list (not a table — tables overflow in the narrow chat pane)
    const lines = [];
    lines.push(`**${compounds.length} compound(s)** — sorted by ${effectiveSortBy}:\n`);

    const missingEnergies = [];

    for (const c of sorted) {
        const parts = [`**Compound ${c.id}**`];
        if (c.isActive) parts[0] += ' (active)';
        parts[0] += ':';

        if (c.energies) {
            if (c.energies.interactionScore != null) {
                parts.push(`Score ${fmt(c.energies.interactionScore)}`);
            }
            if (c.energies.vdW != null) {
                parts.push(`vdW ${fmt(c.energies.vdW)}`);
            }
            if (c.energies.solvation != null) {
                parts.push(`Solv. ${fmt(c.energies.solvation)}`);
            }
            if (c.energies.gninaAffinity != null) {
                parts.push(`GNINA ${fmt(c.energies.gninaAffinity)}`);
            }
        } else {
            parts.push('*no energies calculated*');
            missingEnergies.push(c.id);
        }

        if (c.properties) {
            const propParts = [];
            if (c.properties.MW != null) propParts.push(`MW ${fmt(c.properties.MW, 0)}`);
            if (c.properties.logP != null) propParts.push(`logP ${fmt(c.properties.logP)}`);
            if (propParts.length > 0) parts.push(propParts.join(', '));
        }

        lines.push(parts.join(' | '));
    }

    // Best compound callout
    const best = sorted[0];
    if (best && best.energies?.interactionScore != null) {
        lines.push('');
        lines.push(
            `**Best:** Compound ${best.id} `
            + `(${fmt(best.energies.interactionScore)} kcal/mol)`
        );
    }

    // Missing energies note
    if (missingEnergies.length > 0) {
        lines.push('');
        const ids = missingEnergies.join(', ');
        const verb = missingEnergies.length > 1 ? 'have' : 'has';
        lines.push(
            `Compound${missingEnergies.length > 1 ? 's' : ''} ${ids} `
            + `${verb} no energies. `
            + 'Want me to run **get-energies** on them?'
        );
    }

    lines.push('');
    lines.push('*For a detailed view, type **"open compound table"**.*');

    return { text: lines.join('\n'), missingEnergies };
}

/**
 * Read a potentially nested value from a compound data object.
 * Supports keys like 'interactionScore' (looks in energies),
 * 'MW' (looks in properties), or 'gninaAffinity' (looks in energies).
 */
function getNestedValue(compound, key) {
    if (compound.energies && compound.energies[key] != null) {
        return compound.energies[key];
    }
    if (compound.properties && compound.properties[key] != null) {
        return compound.properties[key];
    }
    if (compound[key] != null) {
        return compound[key];
    }
    return null;
}

/**
 * Parse a filter expression from the user message.
 * E.g., "filter by MW under 500" → { key: 'MW', op: '<', value: 500 }
 *
 * @param {string} userMessage
 * @returns {{key: string, op: string, value: number}|null}
 */
export function parseFilterExpression(userMessage) {
    const lower = userMessage.toLowerCase();

    // Match patterns like "MW under 500", "logP above 3", "energy below -20"
    const patterns = [
        /(?:mw|molecular weight)\s*(?:under|below|<|less than)\s*([\d.]+)/i,
        /(?:mw|molecular weight)\s*(?:over|above|>|greater than)\s*([\d.]+)/i,
        /(?:logp|log\s*p)\s*(?:under|below|<|less than)\s*([\d.]+)/i,
        /(?:logp|log\s*p)\s*(?:over|above|>|greater than)\s*([\d.]+)/i,
        /(?:energy|score)\s*(?:under|below|<|less than|better than)\s*([-\d.]+)/i,
        /(?:energy|score)\s*(?:over|above|>|greater than|worse than)\s*([-\d.]+)/i,
    ];

    const keys = ['MW', 'MW', 'logP', 'logP', 'interactionScore', 'interactionScore'];
    const ops = ['<', '>', '<', '>', '<', '>'];

    for (let i = 0; i < patterns.length; i++) {
        const match = lower.match(patterns[i]);
        if (match) {
            return { key: keys[i], op: ops[i], value: parseFloat(match[1]) };
        }
    }

    return null;
}

/**
 * Apply a filter to compound data and return a summary.
 *
 * @param {Array<Object>} compounds
 * @param {{key: string, op: string, value: number}} filter
 * @returns {string}
 */
export function applyFilter(compounds, filter) {
    const filtered = compounds.filter((c) => {
        const val = getNestedValue(c, filter.key);
        if (val == null) return false;
        return filter.op === '<' ? val < filter.value : val > filter.value;
    });

    if (filtered.length === 0) {
        return `No compounds match the filter ${filter.key} ${filter.op} ${filter.value}.`;
    }

    const header = `**${filtered.length}** of **${compounds.length}** compounds pass`
        + ` the filter (${filter.key} ${filter.op} ${filter.value}):\n\n`;
    const { text } = buildCompoundSummary(filtered, filter.key, filter.op === '<');
    return `${header}${text}`;
}
